import { tracking } from '@iwoca/frontend-tracking-library';
import { IconZoomOutline } from '@iwoca/icons';
import { Input, Spinner } from '@iwoca/orion';
import { plural } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { clsx } from 'clsx';

import styles from './CompanySearch.module.css';
import ButtonLink from '../../../../ButtonLink/ButtonLink';
import { useStageFieldTracking } from '../../../form/MobileSignupFieldTracking';
import { StepLayout } from '../../../StepLayout/StepLayout';
import { TCompanyMatch } from '../companyData';
import { TCompanySearch } from '../useCompanySearch';

// DIT-680/681. Register search: an inline results list rather than the desktop
// Combobox, whose overlay fights the mobile keyboard. Presentational — the search
// lifecycle (debounce, fetch, response tracking) lives in the parent's
// useCompanySearch hook so it survives this screen unmounting on select.
// "Not listed" drops to manual entry.
export const CompanySearch = ({
  search: {
    query,
    setQuery,
    results,
    resultCount,
    isFetching,
    hasSearched,
    searchedCompaniesCount,
  },
  onSelect,
  onEnterManually,
}: {
  search: TCompanySearch;
  onSelect: (company: TCompanyMatch) => void;
  onEnterManually: () => void;
}) => {
  const { t } = useLingui();
  const { fieldProps } = useStageFieldTracking();

  return (
    <StepLayout
      stageTitle={t`Business details`}
      heading={t`What’s your company called?`}
      subHeading={t`We search for your company and fill in your details automatically.`}
      hideCta
      fieldProps={fieldProps}
    >
      <div className={styles.searchScreen}>
        <Input
          iconRight={<IconZoomOutline />}
          placeholder={t`Search a company name or number`}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            tracking.ampli.companySearchInputChanged({
              query_length: event.target.value.length,
            });
          }}
          className="fs-mask"
          name="companyName"
          data-defer-completion="true"
        />
        {(isFetching || hasSearched) && (
          <div
            className={clsx(
              styles.searchStatus,
              isFetching && styles.searchStatusLoading,
            )}
            role="status"
            aria-live="polite"
          >
            {isFetching ? (
              <Spinner />
            ) : (
              plural(resultCount, {
                0: '# results',
                one: '# result – select your company',
                other: '# results – select your company',
              })
            )}
          </div>
        )}
        {!isFetching && results.length > 0 && (
          <ul
            className={styles.results}
            role="listbox"
            aria-label={t`Companies`}
          >
            {results.map((company) => (
              <li key={company.crefo_id} role="option" aria-selected={false}>
                <button
                  type="button"
                  onClick={() => onSelect(company)}
                  className={clsx(styles.result, 'fs-mask', 'dd-privacy-mask')}
                >
                  <span className={styles.resultName}>{company.name}</span>
                  <span>
                    {company.street} {company.house_number}, {company.postcode}{' '}
                    {company.city}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {/* Only offered once the register has actually responded (with or
            without matches) — not while idle or mid-search. */}
        {hasSearched && !isFetching && (
          <ButtonLink
            type="button"
            className={styles.enterManually}
            onClick={() => {
              // Mobile only surfaces this once the register has responded, so it
              // always maps to desktop's 'Not found' button variant.
              tracking.ampli.enterCompanyManuallyClicked({
                searched_companies_count: searchedCompaniesCount,
                version_of_button: 'Not found',
              });
              onEnterManually();
            }}
          >
            {t`My company is not listed →`}
          </ButtonLink>
        )}
      </div>
    </StepLayout>
  );
};
